import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export const DEFAULT_SEARCH_PLACEHOLDER = "Search or ask me anything";

type GlobalSearchContextValue = {
  value: string;
  setValue: (value: string) => void;
  placeholder: string;
  setPlaceholder: (placeholder: string) => void;
};

const GlobalSearchContext = createContext<GlobalSearchContextValue | null>(
  null,
);

/**
 * Wraps the router (see main.tsx), same pattern as `SecondaryHeaderProvider`
 * in secondary-header-context.tsx — lets the ONE real `<SearchInput>` the
 * shell renders (in `<RootLayout>`'s top header) be driven by whichever leaf
 * page route is currently mounted, instead of every page needing its own
 * separate local filter input next to a decorative global search box.
 */
export function GlobalSearchProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState("");
  const [placeholder, setPlaceholder] = useState(DEFAULT_SEARCH_PLACEHOLDER);
  const contextValue = useMemo(
    () => ({ value, setValue, placeholder, setPlaceholder }),
    [value, placeholder],
  );
  return (
    <GlobalSearchContext.Provider value={contextValue}>
      {children}
    </GlobalSearchContext.Provider>
  );
}

function useGlobalSearchContext(): GlobalSearchContextValue {
  const ctx = useContext(GlobalSearchContext);
  if (!ctx) {
    throw new Error(
      "useGlobalSearch(...) must be used within a GlobalSearchProvider",
    );
  }
  return ctx;
}

/** Read by `<RootLayout>` to drive the shell's one real `<SearchInput>`. */
export function useGlobalSearchInputProps(): {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
} {
  const { value, setValue, placeholder } = useGlobalSearchContext();
  return {
    value,
    onChange: (event) => setValue(event.target.value),
    placeholder,
  };
}

/**
 * Called by a leaf page route that wants to filter its own content using
 * the shell's ONE global search box instead of a redundant local input.
 * Sets the box's placeholder to `placeholder` while mounted, and returns
 * its live text. Both the placeholder and the typed text reset on unmount
 * (route change) — the query is conceptually scoped to the page that's
 * reading it, even though the input itself is rendered by `<RootLayout>`.
 */
export function usePageSearch(placeholder: string): string {
  const { value, setPlaceholder, setValue } = useGlobalSearchContext();
  useEffect(() => {
    setPlaceholder(placeholder);
    return () => {
      setPlaceholder(DEFAULT_SEARCH_PLACEHOLDER);
      setValue("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder]);
  return value;
}
